import { useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useNavigate } from 'react-router-dom';
import { MoreHorizontal, UserCog, LogOut, X } from 'lucide-react';
import clsx from 'clsx';
import { useAuth } from '../../context/AuthContext';
import { MyProfileModal } from '../profile/MyProfileModal';
import { NAV_ROUTES } from '../../utils/constants';
import { NAV_ITEMS } from './navItems';

// Bottom nav: máximo 4 tabs primarios + "Más" fijo. Un móvil no tiene el
// ancho de la sidebar para listar hasta 8 ítems (rol admin) como tabs.
const MAX_PRIMARY_ITEMS = 4;

interface MobileNavProps {
  criticalCount?: number;
}

export function MobileNav({ criticalCount = 0 }: MobileNavProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const visibleItems = NAV_ITEMS.filter(item =>
    user ? item.roles.includes(user.role) && (!item.feature || user.features[item.feature]) : false
  );
  const primaryItems = visibleItems.slice(0, MAX_PRIMARY_ITEMS);
  const overflowItems = visibleItems.slice(MAX_PRIMARY_ITEMS);

  const handleLogout = () => {
    setMoreOpen(false);
    logout();
    navigate(NAV_ROUTES.LOGIN);
  };

  return (
    <>
      <nav className="bottom-nav" aria-label="Navegación principal">
        {primaryItems.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => clsx('bottom-nav__item', isActive && 'bottom-nav__item--active')}
          >
            <span className="bottom-nav__icon">
              {item.icon}
              {item.to === NAV_ROUTES.ALERTS && criticalCount > 0 && (
                <span className="bottom-nav__badge">{criticalCount > 9 ? '9+' : criticalCount}</span>
              )}
            </span>
            <span className="bottom-nav__label">{item.label}</span>
          </NavLink>
        ))}
        <button
          type="button"
          className="bottom-nav__item"
          onClick={() => setMoreOpen(true)}
          aria-label="Más opciones"
        >
          <span className="bottom-nav__icon"><MoreHorizontal size={20} /></span>
          <span className="bottom-nav__label">Más</span>
        </button>
      </nav>

      {moreOpen && createPortal(
        <div className="more-sheet-overlay" onClick={() => setMoreOpen(false)}>
          <div className="more-sheet" onClick={e => e.stopPropagation()}>
            <div className="more-sheet__header">
              <span className="more-sheet__title">Más opciones</span>
              <button type="button" className="more-sheet__close" onClick={() => setMoreOpen(false)} aria-label="Cerrar">
                <X size={18} />
              </button>
            </div>
            <div className="more-sheet__list">
              {overflowItems.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMoreOpen(false)}
                  className={({ isActive }) => clsx('more-sheet__item', isActive && 'more-sheet__item--active')}
                >
                  <span className="more-sheet__icon">{item.icon}</span>
                  <span>{item.label}</span>
                  {item.to === NAV_ROUTES.ALERTS && criticalCount > 0 && (
                    <span className="more-sheet__badge">{criticalCount}</span>
                  )}
                </NavLink>
              ))}
              {overflowItems.length > 0 && <div className="more-sheet__divider" />}
              <button
                type="button"
                className="more-sheet__item"
                onClick={() => { setMoreOpen(false); setProfileOpen(true); }}
              >
                <span className="more-sheet__icon"><UserCog size={18} /></span>
                <span>Mi perfil</span>
              </button>
              <button type="button" className="more-sheet__item more-sheet__item--danger" onClick={handleLogout}>
                <span className="more-sheet__icon"><LogOut size={18} /></span>
                <span>Cerrar sesión</span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <MyProfileModal isOpen={profileOpen} onClose={() => setProfileOpen(false)} />
    </>
  );
}
